import { useEffect, useRef, useState } from 'react';

/**
 * Fires once when the target element enters the viewport, then disconnects.
 * Used to defer expensive per-row data fetches (e.g. a Firestore query fired
 * for every row of a large table) until the row is actually visible, instead
 * of firing N queries for every row on initial page load.
 *
 * Once `inView` flips true it stays true — the caller's data fetch (typically
 * a React Query hook with its own staleTime) keeps the result cached, so
 * there's no need to re-observe after the first intersection.
 *
 * `options` is an effect dependency by reference — pass a stable object
 * (module-level constant or memoized with `useMemo`), not an inline object
 * literal, or the observer will tear down and recreate every render until
 * the element comes into view.
 */
export function useInViewOnce<T extends HTMLElement>(options?: IntersectionObserverInit) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      // Environments without IntersectionObserver support (older browsers,
      // some test runners) — fail open rather than never loading the data.
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setInView(true);
        observer.disconnect();
      }
    }, { rootMargin: '200px', ...options });

    observer.observe(node);
    return () => observer.disconnect();
  }, [inView, options]);

  return { ref, inView };
}
